import type { NextPage } from 'next';
import { useState } from 'react';
import { useForm, SubmitHandler, FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DotPulse } from '@uiball/loaders';

import { notificationToaster } from '@/utils/index';
import { contactFormValidationSchema } from '@/validation';
import { ToasterType } from '@/types/components/toaster.type';

import { Input, Textarea } from '@/components/shared/Inputs';
import { Button } from '@/components/shared/Buttons';
import { SvgEnvelopeIcon } from '@/components/svg';
import { LandingLayout } from '@/components/layouts';
import { Fetch } from '../services';
import { IContactForm } from '../types';

const ContactAndSupport: NextPage = () => {
  const [loading, setLoading] = useState(false);

  const methods: UseFormReturn = useForm({
    resolver: yupResolver(contactFormValidationSchema),
    mode: 'onSubmit',
  });

  const { handleSubmit, reset } = methods;

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    setLoading(true);
    try {
      await Fetch.getInTouch(data as IContactForm);
      setLoading(false);

      notificationToaster(
        ToasterType.SUCCESS,
        'Thank you, we will contact you as soon as possible!'
      );
      reset();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <LandingLayout isWithButton>
      <div className="relative flex justify-center py-40">
        <div className="m-0 flex w-full max-w-7xl flex-col bg-white/10 sm:mx-8 sm:my-12 sm:rounded-2xl lg:flex-row">
          <div className="relative bg-white/[4%] px-6 pb-12 pt-12 lg:max-w-[405px] lg:px-8">
            <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
              <h2 className="font-stag text-[34px] font-semibold leading-[40px] text-white">
                Get in touch
              </h2>
              <p className="mt-6 text-base leading-6 text-gray-300">
                We appreciate your interest and would be happy to assist you. Our team will get back
                to you within one business day.
              </p>
              <dl className="mt-10 space-y-4 text-base leading-6 text-gray-300">
                <div className="flex w-min gap-x-4 rounded-lg bg-white/10 p-4 font-stag font-bold hover:text-white">
                  <dt className="flex-none">
                    <span className="sr-only">Email</span>
                    <SvgEnvelopeIcon />
                  </dt>
                  <dd>
                    <a href="mailto:hello@rushfit.com">hello@rushfit.com</a>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="w-full px-6 pb-12 pt-12 lg:px-8">
              <div className="mx-auto lg:mr-0">
                <h2 className="mb-6 font-stag text-[34px] font-semibold leading-[40px] text-white">
                  Send us a message
                </h2>
                <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2 sm:gap-y-4">
                  <Input
                    id="full_name"
                    name="full_name"
                    label="Full name"
                    type="text"
                    placeholder=""
                    defaultValue=""
                  />
                  <Input
                    id="email"
                    name="email"
                    label="Email"
                    type="email"
                    placeholder=""
                    defaultValue=""
                  />
                  <div className="sm:col-span-2">
                    <Input
                      id="subject"
                      name="subject"
                      label="Subject"
                      type="text"
                      placeholder=""
                      defaultValue=""
                    />
                  </div>

                  <div className="relative sm:col-span-2">
                    <span className="absolute top-0 right-0 font-stag text-sm text-neutrals-mid">
                      Max. 500 characters
                    </span>
                    <Textarea
                      id="message"
                      name="message"
                      defaultValue=""
                      placeholder=""
                      label="Message"
                      maxLength={500}
                      resize
                    />
                  </div>
                </div>
                <div className="mt-8 flex justify-end">
                  <Button type="submit" size="large" className="w-full sm:w-[100px]">
                    {loading ? <DotPulse size={20} speed={1.3} color="white" /> : 'Submit'}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </LandingLayout>
  );
};

export default ContactAndSupport;
